import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Artwork } from "../types/artwork";
import { getStatusColor, getStatusText } from "../utils";
import "./MapView.css";

// Fix per le icone di Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface MapViewProps {
  artworks: Artwork[];
  onArtworkSelect?: (artwork: Artwork) => void;
  showLegend?: boolean;
  showStats?: boolean;
  height?: string;
}

const MapView: React.FC<MapViewProps> = ({
  artworks,
  onArtworkSelect,
  showLegend = true,
  showStats = true,
  height = "400px",
}) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const center: [number, number] = [37.5, 14.0]; // Centro della Sicilia

  const artworksWithCoordinates = artworks.filter(
    (artwork) => artwork.coordinates
  );

  useEffect(() => {
    // Simulate map loading
    const timer = setTimeout(() => {
      setMapLoaded(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Group artworks by location (same coordinates or very close)
  const groupArtworksByLocation = (artworks: Artwork[]) => {
    const groups: { [key: string]: Artwork[] } = {};
    const tolerance = 0.001; // ~100 metri di tolleranza

    artworks.forEach((artwork) => {
      if (!artwork.coordinates) return;

      // Trova se esiste già un gruppo con coordinate simili
      let groupKey = "";
      for (const existingKey in groups) {
        const [lat, lng] = existingKey.split(",").map(Number);
        const distance = Math.sqrt(
          Math.pow(artwork.coordinates.lat - lat, 2) +
            Math.pow(artwork.coordinates.lng - lng, 2)
        );

        if (distance <= tolerance) {
          groupKey = existingKey;
          break;
        }
      }

      // Se non trova un gruppo esistente, crea uno nuovo
      if (!groupKey) {
        groupKey = `${artwork.coordinates.lat},${artwork.coordinates.lng}`;
        groups[groupKey] = [];
      }

      groups[groupKey].push(artwork);
    });

    return groups;
  };

  const locationGroups = groupArtworksByLocation(artworksWithCoordinates);

  // Create custom icons based on artwork count and status
  const createCustomIcon = (artworks: Artwork[]) => {
    const count = artworks.length;
    const primaryStatus = artworks[0].status;
    const color = getStatusColor(primaryStatus);

    // Se c'è solo un artwork, usa l'icona semplice
    if (count === 1) {
      return L.divIcon({
        className: "custom-marker single",
        html: `<div class="marker-single" style="
          background: ${color};
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          position: relative;
        "></div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });
    }

    // Per più artworks, crea un'icona con contatore
    const statusColors = artworks.map((a) => getStatusColor(a.status));
    const uniqueColors = Array.from(new Set(statusColors));

    return L.divIcon({
      className: "custom-marker multiple",
      html: `
        <div class="marker-multiple" style="
          background: ${color};
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 3px 12px rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 12px;
          position: relative;
        ">
          ${count}
          ${
            uniqueColors.length > 1
              ? '<div class="marker-mixed-indicator"></div>'
              : ""
          }
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });
  };

  // Create cluster icon
  const createClusterCustomIcon = (cluster: any) => {
    const count = cluster.getChildCount();
    let className = "marker-cluster-small";

    if (count < 10) {
      className = "marker-cluster-small";
    } else if (count < 100) {
      className = "marker-cluster-medium";
    } else {
      className = "marker-cluster-large";
    }

    return L.divIcon({
      html: `<div class="cluster-inner"><span>${count}</span></div>`,
      className: `marker-cluster ${className}`,
      iconSize: L.point(40, 40, true),
    });
  };

  // Get status statistics
  const statusStats = {
    pending: artworksWithCoordinates.filter((a) => a.status === "pending")
      .length,
    "in-progress": artworksWithCoordinates.filter(
      (a) => a.status === "in-progress"
    ).length,
    obtained: artworksWithCoordinates.filter((a) => a.status === "obtained")
      .length,
  };

  return (
    <div className="map-view-container" style={{ height }}>
      {/* Loading State */}
      {!mapLoaded && (
        <div className="map-view-loading">
          <div className="map-view-loading-content">
            <div className="map-view-loading-icon">🗺️</div>
            <p className="map-view-loading-text">Loading map...</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {mapLoaded && artworksWithCoordinates.length === 0 && (
        <div className="map-view-empty">
          <div className="map-view-empty-icon">📍</div>
          <h4 className="map-view-empty-title">No Locations to Show</h4>
          <p className="map-view-empty-description">
            Add GPS coordinates to your artworks to see them on the map
          </p>
        </div>
      )}

      {/* Map */}
      {mapLoaded && (
        <>
          <MapContainer center={center} zoom={8} className="map-view-leaflet">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            <MarkerClusterGroup
              chunkedLoading
              iconCreateFunction={createClusterCustomIcon}
              maxClusterRadius={50} // Raggruppa marker entro 50 pixel
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              zoomToBoundsOnClick={true}
            >
              {Object.entries(locationGroups).map(
                ([locationKey, groupArtworks]) => {
                  const [lat, lng] = locationKey.split(",").map(Number);

                  return (
                    <Marker
                      key={locationKey}
                      position={[lat, lng]}
                      icon={createCustomIcon(groupArtworks)}
                    >
                      <Popup maxWidth={400} className="custom-popup">
                        <div className="map-popup-container">
                          {/* Header */}
                          <div className="map-popup-header">
                            <h3 className="map-popup-title">
                              <span className="map-popup-title-icon">📍</span>
                              {groupArtworks.length === 1
                                ? groupArtworks[0].title
                                : `${groupArtworks.length} Artworks at this location`}
                            </h3>
                            {groupArtworks.length === 1 && (
                              <p className="map-popup-artist">
                                <span className="map-popup-artist-icon">
                                  👨‍🎨
                                </span>
                                {groupArtworks[0].artist}
                              </p>
                            )}
                          </div>

                          {/* Location Info */}
                          <div className="map-popup-location-info">
                            <div className="map-popup-detail-item">
                              <span className="map-popup-detail-icon">📍</span>
                              <span className="map-popup-detail-label">
                                Location:
                              </span>
                              <span className="map-popup-detail-value">
                                {groupArtworks[0].location}
                              </span>
                            </div>

                            <div className="map-popup-detail-item">
                              <span className="map-popup-detail-icon">🏛️</span>
                              <span className="map-popup-detail-label">
                                Institution:
                              </span>
                              <span className="map-popup-detail-value">
                                {groupArtworks[0].institution}
                              </span>
                            </div>
                          </div>

                          {/* Artworks List */}
                          <div className="map-popup-artworks">
                            {groupArtworks.map((artwork, index) => (
                              <div
                                key={artwork.id || index}
                                className="map-popup-artwork-item"
                              >
                                <div className="map-popup-artwork-header">
                                  <div className="map-popup-artwork-info">
                                    <h4 className="map-popup-artwork-title">
                                      🎨 {artwork.title}
                                    </h4>
                                    <p className="map-popup-artwork-artist">
                                      👨‍🎨 {artwork.artist}
                                    </p>
                                  </div>

                                  <div className="map-popup-artwork-badges">
                                    <span
                                      className={`map-popup-status ${artwork.status}`}
                                      style={{
                                        backgroundColor: getStatusColor(
                                          artwork.status
                                        ),
                                      }}
                                    >
                                      {getStatusText(artwork.status)}
                                    </span>

                                    {artwork.isDigitized && (
                                      <span className="map-popup-digitized-badge">
                                        🖼️ Digitized
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {artwork.notes && (
                                  <div className="map-popup-artwork-notes">
                                    <span className="map-popup-notes-icon">
                                      �
                                    </span>
                                    <span className="map-popup-notes-text">
                                      {artwork.notes.length > 100
                                        ? `${artwork.notes.substring(
                                            0,
                                            100
                                          )}...`
                                        : artwork.notes}
                                    </span>
                                  </div>
                                )}

                                {/* Action Button */}
                                {onArtworkSelect && (
                                  <div className="map-popup-artwork-actions">
                                    <button
                                      className="map-popup-action-button view"
                                      onClick={() => onArtworkSelect(artwork)}
                                    >
                                      <span>👁️</span>
                                      View Details
                                    </button>
                                  </div>
                                )}

                                {/* Divider (except for last item) */}
                                {index < groupArtworks.length - 1 && (
                                  <div className="map-popup-artwork-divider"></div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Summary for multiple artworks */}
                          {groupArtworks.length > 1 && (
                            <div className="map-popup-summary">
                              <div className="map-popup-summary-stats">
                                {Object.entries(
                                  groupArtworks.reduce((acc, artwork) => {
                                    acc[artwork.status] =
                                      (acc[artwork.status] || 0) + 1;
                                    return acc;
                                  }, {} as Record<string, number>)
                                ).map(([status, count]) => (
                                  <span
                                    key={status}
                                    className="map-popup-summary-stat"
                                    style={{
                                      backgroundColor: getStatusColor(status),
                                    }}
                                  >
                                    {getStatusText(status)}: {count}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  );
                }
              )}
            </MarkerClusterGroup>
          </MapContainer>

          {/* Legend */}
          {showLegend && artworksWithCoordinates.length > 0 && (
            <div className="map-view-legend">
              <h4 className="map-view-legend-title">
                <span>📊</span>
                Status Legend
              </h4>
              <div className="map-view-legend-items">
                {Object.entries(statusStats).map(
                  ([status, count]) =>
                    count > 0 && (
                      <div key={status} className="map-view-legend-item">
                        <div
                          className="map-view-legend-marker"
                          style={{ backgroundColor: getStatusColor(status) }}
                        ></div>
                        <span className="map-view-legend-label">
                          {getStatusText(status)} ({count})
                        </span>
                      </div>
                    )
                )}
              </div>

              {/* Legend for grouped markers */}
              <div className="map-view-legend-divider"></div>
              <div className="map-view-legend-grouped">
                <div className="map-view-legend-item">
                  <div className="map-view-legend-marker-multiple">2+</div>
                  <span className="map-view-legend-label">
                    Multiple artworks at same location
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Stats */}
          {showStats && artworksWithCoordinates.length > 0 && (
            <div className="map-view-stats">
              <div className="map-view-stats-content">
                <span className="map-view-stats-icon">📍</span>
                <span>
                  {artworksWithCoordinates.length} artworks in{" "}
                  {Object.keys(locationGroups).length} locations
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MapView;
